import React from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import * as Toast from '@radix-ui/react-toast';
import { spacing, colors, deviceSizes } from '../../style';
import fonts from '../../style/fonts';

// components
import { Flex } from '../box';
import Text from '../text';
import { RoundButton } from '../button';
import Icon from '../icon';

export type NotificationToastKind = 'success' | 'error' | 'warning' | 'info' | 'danger';

export interface NotificationToastProps {
  message: React.ReactNode | string;
  onClose?: () => void;
  className?: string;
  kind?: NotificationToastKind;
}

const statusIconByKind: Partial<Record<NotificationToastKind, React.ComponentProps<typeof Icon>['name']>> = {
  success: 'CHECK_CIRCLE',
  error: 'GPP_BAD',
};

const NotificationToastComponent = ({ message, className, onClose, kind }: NotificationToastProps) => {
  const isStatusToast = kind === 'success' || kind === 'error';
  const statusIcon = kind ? statusIconByKind[kind] : undefined;

  return (
    <Toast.Root open={true}>
      <section
        className={classNames('box-alert-notification', className, kind && `kind-${kind}`, {
          'text-center': !isStatusToast,
        })}
      >
        <Flex
          display={'inline-flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
          className={'inner-container'}
          gap={'md'}
        >
          <Flex alignItems={isStatusToast ? 'flex-start' : 'center'} gap="sm" flex={1} minWidth={0}>
            {statusIcon && <Icon name={statusIcon} size="xl" variant="gray900" flexShrink={0} />}
            <Toast.Title asChild>
              <Text
                weight={isStatusToast ? 400 : 500}
                align={isStatusToast ? 'left' : 'center'}
                variant={isStatusToast ? 'gray900' : 'white'}
                className="toast-message"
              >
                {message}
              </Text>
            </Toast.Title>
          </Flex>

          {onClose && (
            <Toast.Action altText={'close'} asChild>
              <RoundButton
                variant={isStatusToast ? 'secondary-clean' : 'secondary'}
                aria-label={'close'}
                onClick={onClose}
                flexShrink={0}
                alignSelf={isStatusToast ? 'flex-start' : 'center'}
                size={'xs'}
              >
                <Icon name={'CLOSE'} clickable />
              </RoundButton>
            </Toast.Action>
          )}
        </Flex>
      </section>
    </Toast.Root>
  );
};

const NotificationToast = styled(NotificationToastComponent)`
  left: 0;
  right: 0;
  margin: auto;
  margin-bottom: ${spacing.xs};
  z-index: 9999;
  display: flex;

  .inner-container {
    position: relative;
    max-width: 620px;
    background: ${colors.gray900};
    border-radius: 4px;
    margin: auto;
    box-shadow: 0 8px 8px -4px rgba(0, 0, 0, 0.12);
    padding: 16px;

    @media (max-width: ${deviceSizes.sm}) {
      min-width: auto;
    }
  }

  .toast-message {
    font-family: ${({ theme }) => fonts[theme?.textFontFamily || 'roboto']};
  }

  &.kind-success .inner-container {
    background: ${colors.positiveBackground};
  }

  &.kind-error .inner-container {
    background: ${colors.negativeBackground};
  }

  &.kind-success,
  &.kind-error {
    .toast-message {
      color: ${colors.gray900};
      font-weight: 400;
    }
  }
`;

export default NotificationToast;
